import { enqueueSnackbar } from 'notistack';
import { useCallback } from 'react';
import { GET_WAREHOUSE_INFO } from 'src/api/warehouses.api';
import { notifyUnlessSurfaced } from 'src/lib/apollo/appErrorLink';
import type { CreatePlaceInput, UpdatePlaceInput } from 'types/graphql';
import { useCreatePlace } from './useCreatePlace';
import { useUpdatePlace } from './useUpdatePlace';

/** The warehouse modal's place create: a snackbar on success, one failure toast (the Apollo error link's, else ours); resolves the new id. */
export function useCreateWarehousePlace() {
  const { create } = useCreatePlace({
    onCompleted: () => {
      enqueueSnackbar('Place created successfully');
    },
    onError: (error) => notifyUnlessSurfaced(error, 'Failed to create the place.'),
  });

  return useCallback(
    async (input: CreatePlaceInput) => {
      const { data } = await create(input);
      return data?.res.id;
    },
    [create]
  );
}

/** The warehouse modal's place update: refetches the warehouse page, snackbar on success, one failure toast (the Apollo error link's, else ours). */
export function useUpdateWarehousePlace() {
  const { update } = useUpdatePlace({
    refetchQueries: [GET_WAREHOUSE_INFO],
    onCompleted: () => {
      enqueueSnackbar('Place updated successfully');
    },
    onError: (error) => notifyUnlessSurfaced(error, 'Failed to update the place.'),
  });

  return useCallback(
    async (id: number, input: UpdatePlaceInput) => {
      const { data } = await update({ id, input });
      return data?.updatePlace;
    },
    [update]
  );
}
